import { LoadingSpinner, EmptyState } from '../shared/components/ui'
import GuestUserRow from './GuestUserRow'
import type { GroupedGuestUser } from '../types/guest'

interface GuestUserListProps {
  users: GroupedGuestUser[]
  isLoading?: boolean
  hasLoadedOnce?: boolean
  error?: string | null
  onToggleDevice: (guestEmail: string, deviceId: number, grant: boolean) => Promise<void>
  onRevokeAll: (guestEmail: string) => void
  onResendInvite: (guestEmail: string) => void
  onDeleteRevoked: (guestEmail: string) => void
}

export default function GuestUserList({
  users,
  isLoading = false,
  hasLoadedOnce = false,
  error = null,
  onToggleDevice,
  onRevokeAll,
  onResendInvite,
  onDeleteRevoked,
}: GuestUserListProps) {
  // Show loading spinner on initial load (before data has ever loaded)
  if (isLoading && !hasLoadedOnce) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Guests</h3>
        <p className="text-red-700">{error}</p>
      </div>
    )
  }

  // Only show empty state after initial load is complete
  if (users.length === 0 && hasLoadedOnce) {
    return (
      <EmptyState
        title="No guests invited yet"
        description="Invite guests to share device access. They'll receive an email with instructions."
      />
    )
  }

  // Still loading initial data but users array is empty
  if (users.length === 0 && !hasLoadedOnce) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {users.map((user) => (
        <GuestUserRow
          key={user.email}
          user={user}
          onToggleDevice={onToggleDevice}
          onRevokeAll={onRevokeAll}
          onResendInvite={onResendInvite}
          onDeleteRevoked={onDeleteRevoked}
        />
      ))}
    </div>
  )
}
